import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, Avatar, Skeleton, Chip, Button, LinearProgress,
    Drawer, Divider, IconButton, Tooltip, TextField, Autocomplete,
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import ChatIcon from '@mui/icons-material/Chat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/es';

import {
    fetchAgentStatsFromAPI,
    fetchAgentConversationsFromAPI,
    clearAgentConversations,
    analyzeAgentFromAPI,
    clearAgentAnalysis,
} from '../../Redux/InformesAIDucks';
import { pollConversationMessages } from '../../Redux/ConversationDucks';
import { fetchBusinessesFromAPI } from '../../Redux/BusinessDucks';
import WhatsAppChat from '../../components/MessagingView/ChatInterface';

dayjs.extend(utc);
dayjs.locale('es');

// ── Helpers ───────────────────────────────────────────────────────────────────

const getUserBusinessLineIds = () => {
    const cookies = document.cookie.split('; ');
    const perfilCookie = cookies.find(c => c.startsWith('perfil='));
    const perfil = perfilCookie ? decodeURIComponent(perfilCookie.split('=')[1]) : '';
    if (perfil === 'Super-Admin') return null;
    const blCookie = cookies.find(c => c.startsWith('businessLineIds='));
    if (!blCookie) return [];
    return blCookie.split('=')[1].split(',').map(Number).filter(Boolean);
};

const getInitials = (name) => {
    if (!name?.trim()) return '??';
    return name.trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('');
};

const fmtDate = (d) => dayjs.utc(d).local().format('DD/MM/YY HH:mm');

const fmtPhone = (tel) => {
    if (!tel) return '';
    const s = String(tel);
    return s.length > 4 ? `+(${s.substring(0, 2)}) ${s.substring(2)}` : s;
};

const feelingColor = (label) => {
    const l = (label || '').toLowerCase();
    if (l.includes('satisf')) return '#22c55e';
    if (l.includes('molest') || l.includes('insatisf') || l.includes('negativ')) return '#ef4444';
    return '#f59e0b';
};

const toNumberOrNull = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const getSummaryDelta = (report, key) => {
    const capitalized = key.charAt(0).toUpperCase() + key.slice(1);
    const candidates = [
        report?.deltas?.[key],
        report?.delta?.[key],
        report?.comparison?.[`${key}Delta`],
        report?.comparison?.[`${key}DeltaPct`],
        report?.[`${key}Delta`],
        report?.[`${key}DeltaPct`],
        report?.[`${key}ChangePct`],
        report?.[`delta${capitalized}`],
        report?.[`delta${capitalized}Pct`],
    ];
    for (const candidate of candidates) {
        const parsed = toNumberOrNull(candidate);
        if (parsed !== null) return parsed;
    }
    return null;
};

const formatDeltaPercent = (delta) => {
    const abs = Math.abs(delta);
    return Number.isInteger(abs) ? String(abs) : abs.toFixed(1);
};

// ── Periodos rápidos ──────────────────────────────────────────────────────────

const PERIODS = [
    { label: 'Hoy', getDates: () => [dayjs().startOf('day'), dayjs().endOf('day')] },
    { label: 'Ayer', getDates: () => [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')] },
    { label: '7 días', getDates: () => [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')] },
    { label: '30 días', getDates: () => [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')] },
];

// ── Componente principal ──────────────────────────────────────────────────────

const InformesAIPage = () => {
    const dispatch = useDispatch();
    const userBLIds = getUserBusinessLineIds();

    const allBusinessLines = useSelector(store => store.business);
    const { report, loading, agentConversations, loadingConversations, analysis, analyzing } = useSelector(store => store.informesAI);

    const businessLines = userBLIds === null
        ? (Array.isArray(allBusinessLines) ? allBusinessLines : [])
        : (Array.isArray(allBusinessLines) ? allBusinessLines.filter(bl => userBLIds.includes(bl.id)) : []);

    // Filtros
    const [selectedBL, setSelectedBL] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(0);
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [useCustomRange, setUseCustomRange] = useState(false);

    // Nivel 2: conversaciones del agente
    const [drawerAgentOpen, setDrawerAgentOpen] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(null);

    // Nivel 3: chat de conversación
    const [drawerChatOpen, setDrawerChatOpen] = useState(false);
    const [selectedConv, setSelectedConv] = useState(null);
    const businessLineOptions = [
        ...(userBLIds === null ? [{ id: null, label: 'Todas' }] : []),
        ...businessLines.map(bl => ({
            id: bl.id,
            label: bl.name || bl.nombre || bl.siglas || `BL ${bl.id}`,
        })),
    ];
    const selectedBusinessLineOption =
        businessLineOptions.find(option => option.id === selectedBL) || null;

    const getDateRange = useCallback(() => {
        if (useCustomRange && customFrom && customTo) {
            return [dayjs(customFrom).startOf('day'), dayjs(customTo).endOf('day')];
        }
        return PERIODS[selectedPeriod].getDates();
    }, [useCustomRange, customFrom, customTo, selectedPeriod]);

    const loadReport = useCallback(() => {
        const [from, to] = getDateRange();
        dispatch(fetchAgentStatsFromAPI(from.toDate(), to.toDate(), selectedBL));
    }, [dispatch, getDateRange, selectedBL]);

    useEffect(() => {
        dispatch(fetchBusinessesFromAPI());
    }, [dispatch]);

    useEffect(() => {
        loadReport();
    }, [loadReport]);

    // ── Nivel 2: abrir agente ─────────────────────────────────────────────────

    const handleOpenAgent = (agent) => {
        setSelectedAgent(agent);
        setDrawerAgentOpen(true);
        dispatch(clearAgentConversations());
        dispatch(clearAgentAnalysis());
        const [from, to] = getDateRange();
        dispatch(fetchAgentConversationsFromAPI(agent.agentUserId, from.toDate(), to.toDate(), selectedBL));
    };

    const handleAnalyzeAgent = () => {
        if (!selectedAgent) return;
        const [from, to] = getDateRange();
        dispatch(analyzeAgentFromAPI(selectedAgent.agentUserId, from.toDate(), to.toDate(), selectedBL));
    };

    const handleCloseAgent = () => {
        setDrawerAgentOpen(false);
        setSelectedAgent(null);
        setDrawerChatOpen(false);
        setSelectedConv(null);
        dispatch(clearAgentAnalysis());
    };

    // ── Nivel 3: abrir chat ───────────────────────────────────────────────────

    const handleOpenChat = (conv) => {
        if (!conv.conversationId) return;
        setSelectedConv(conv);
        setDrawerChatOpen(true);
        dispatch(pollConversationMessages(conv.conversationId));
    };

    const handleCloseChat = () => {
        setDrawerChatOpen(false);
        setSelectedConv(null);
    };

    // ── Skeleton ──────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[...Array(4)].map((_, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'center',
                        p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
                        <Skeleton variant="circular" width={44} height={44} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton width="30%" height={20} />
                            <Skeleton width="60%" height={16} sx={{ mt: 0.5 }} />
                        </Box>
                        <Skeleton width={80} height={36} sx={{ borderRadius: 1 }} />
                    </Box>
                ))}
            </Box>
        );
    }

    // ── Resumen global ────────────────────────────────────────────────────────

    const summary = report ? {
        totalAgents: report.totalAgents ?? 0,
        totalConversations: report.totalConversations ?? 0,
        completedConversations: report.completedConversations ?? 0,
        activeConversations: report.activeConversations ?? 0,
    } : null;

    const agents = report?.agents ?? [];

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#fff' }}>

            {/* ── Header ── */}
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>Informes AI</Typography>

                {/* Filtro BL */}
                {businessLineOptions.length > 1 && (
                    <Box sx={{ mb: 1.5, maxWidth: 340 }}>
                        <Autocomplete
                            size="small"
                            disablePortal
                            disableClearable
                            options={businessLineOptions}
                            value={selectedBusinessLineOption}
                            getOptionLabel={(option) => option?.label || ''}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            onChange={(_, option) => setSelectedBL(option?.id ?? null)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Línea de negocio"
                                    placeholder="Seleccionar..."
                                />
                            )}
                            sx={{
                                '& .MuiInputBase-root': { fontSize: 13 },
                                '& .MuiInputLabel-root': { fontSize: 13 },
                            }}
                        />
                    </Box>
                )}

                {/* Selector de período */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {PERIODS.map((p, i) => (
                        <Chip
                            key={p.label}
                            label={p.label}
                            size="small"
                            onClick={() => { setSelectedPeriod(i); setUseCustomRange(false); }}
                            sx={{
                                fontWeight: 600, fontSize: 12,
                                bgcolor: !useCustomRange && selectedPeriod === i ? '#111827' : '#f3f4f6',
                                color: !useCustomRange && selectedPeriod === i ? '#fff' : '#374151',
                                '&:hover': { bgcolor: !useCustomRange && selectedPeriod === i ? '#374151' : '#e5e7eb' },
                            }}
                        />
                    ))}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                            type="date"
                            size="small"
                            value={customFrom}
                            onChange={e => { setCustomFrom(e.target.value); setUseCustomRange(true); }}
                            sx={{
                                width: 170,
                                '& .MuiInputBase-input': {
                                    fontSize: 14,
                                    py: 0.8,
                                },
                                '& .MuiInputBase-input::-webkit-calendar-picker-indicator': {
                                    width: 14,
                                    height: 14,
                                },
                            }}
                        />
                        <Typography variant="caption" color="text.secondary">—</Typography>
                        <TextField
                            type="date"
                            size="small"
                            value={customTo}
                            onChange={e => { setCustomTo(e.target.value); setUseCustomRange(true); }}
                            sx={{
                                width: 170,
                                '& .MuiInputBase-input': {
                                    fontSize: 14,
                                    py: 0.8,
                                },
                                '& .MuiInputBase-input::-webkit-calendar-picker-indicator': {
                                    width: 14,
                                    height: 14,
                                },
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* ── Tarjetas de resumen ── */}
            {summary && (
                <Box sx={{ display: 'flex', gap: 2, px: 3, py: 2, flexShrink: 0, flexWrap: 'wrap' }}>
                    {[
                        { key: 'totalAgents', label: 'Agentes', value: summary.totalAgents, color: '#6366f1' },
                        { key: 'totalConversations', label: 'Conversaciones tomadas', value: summary.totalConversations, color: '#007AFF' },
                        { key: 'completedConversations', label: 'Completadas', value: summary.completedConversations, color: '#22c55e' },
                        { key: 'activeConversations', label: 'Activas ahora', value: summary.activeConversations, color: '#f59e0b' },
                    ].map(card => (
                        (() => {
                            const delta = getSummaryDelta(report, card.key);
                            const hasDelta = delta !== null;
                            const deltaColor = delta > 0 ? '#16a34a' : delta < 0 ? '#dc2626' : '#6b7280';
                            const deltaArrow = delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
                            return (
                        <Box key={card.label} sx={{
                            flex: '1 1 140px', p: 2, borderRadius: 2,
                            border: `1.5px solid ${card.color}22`,
                            bgcolor: `${card.color}0d`,
                        }}>
                            <Typography variant="h5" fontWeight={700} color={card.color}>{card.value}</Typography>
                            <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                            {hasDelta && (
                                <Typography sx={{ mt: 0.25, fontSize: 12, color: deltaColor, fontWeight: 600 }}>
                                    {`${deltaArrow} ${formatDeltaPercent(delta)}% vs período anterior`}
                                </Typography>
                            )}
                        </Box>
                            );
                        })()
                    ))}
                </Box>
            )}

            <Divider />

            {/* ── Tabla de agentes ── */}
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2, bgcolor: '#f8fafc' }}>
                {agents.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', height: 200, gap: 1.5, color: 'text.secondary' }}>
                        <BarChartIcon sx={{ fontSize: 48, opacity: 0.25 }} />
                        <Typography variant="body2">Sin datos para el período seleccionado</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {/* Encabezado */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 2fr auto',
                            gap: 2, px: 2, py: 1, borderRadius: 1.5, bgcolor: '#f9fafb' }}>
                            <Typography variant="caption" fontWeight={700}
                                color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 11 }}>
                                Agente
                            </Typography>
                            <Typography variant="caption" fontWeight={700}
                                color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 11 }}>
                                Tomadas
                            </Typography>
                            <Typography variant="caption" fontWeight={700}
                                color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 11 }}>
                                Completadas
                            </Typography>
                            <Typography variant="caption" fontWeight={700}
                                color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 11 }}>
                                Activas
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <Typography variant="caption" fontWeight={700}
                                    color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 11 }}>
                                    Tasa
                                </Typography>
                                <Tooltip title="Porcentaje de conversaciones completadas sobre el total tomadas en el período">
                                    <InfoOutlinedIcon sx={{ fontSize: 13, color: '#9ca3af', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                            <Typography variant="caption" fontWeight={700}
                                color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 11 }}>
                            </Typography>
                        </Box>

                        {agents.map(agent => (
                            <Box
                                key={`${agent.agentUserId}-${agent.businessLineId}`}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 1fr 1fr 1fr 2fr auto',
                                    gap: 2, px: 2, py: 1.5,
                                    borderRadius: 2,
                                    border: '1px solid #dbe3ee',
                                    borderLeft: '4px solid #dbeafe',
                                    bgcolor: '#fff',
                                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
                                    alignItems: 'center',
                                    '&:hover': {
                                        bgcolor: '#f8fbff',
                                        borderColor: '#c7d4e3',
                                        boxShadow: '0 2px 4px rgba(15, 23, 42, 0.08)',
                                    },
                                }}
                            >
                                {/* Agente */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar sx={{ width: 36, height: 36, fontSize: 14,
                                        bgcolor: '#007AFF22', color: '#007AFF', fontWeight: 700 }}>
                                        {getInitials(agent.agentName)}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600} fontSize={13}>
                                            {agent.agentName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {agent.businessLineName}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Tomadas */}
                                <Typography variant="body2" fontWeight={600}>{agent.totalConversations}</Typography>

                                {/* Completadas */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CheckCircleIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                                    <Typography variant="body2" fontWeight={600}>{agent.completedConversations}</Typography>
                                </Box>

                                {/* Activas */}
                                <Typography variant="body2" fontWeight={600} color="#f59e0b">
                                    {agent.activeConversations}
                                </Typography>

                                {/* Tasa */}
                                {(() => {
                                    const completionRate = Number(agent.completionRate ?? 0);
                                    const completedConversations = Number(agent.completedConversations ?? 0);
                                    const activeConversations = Number(agent.activeConversations ?? 0);
                                    const showPendingRate = completionRate === 0 && completedConversations === 0 && activeConversations > 0;
                                    const showDangerZero = !showPendingRate && completionRate === 0;

                                    return (
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                                                {showPendingRate ? (
                                                    <Tooltip title="Solo se calcula al cerrar conversaciones">
                                                        <Typography variant="caption" sx={{ color: '#6b7280', cursor: 'help' }}>
                                                            —
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="caption" sx={{ color: showDangerZero ? '#dc2626' : 'text.secondary' }}>
                                                        {`${completionRate}%`}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {!showPendingRate && (
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={completionRate}
                                                    sx={{
                                                        height: 6, borderRadius: 3,
                                                        bgcolor: '#e5e7eb',
                                                        '& .MuiLinearProgress-bar': {
                                                            bgcolor: completionRate >= 75 ? '#22c55e'
                                                                : completionRate >= 40 ? '#f59e0b'
                                                                : '#ef4444',
                                                            borderRadius: 3,
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    );
                                })()}

                                {/* Acción */}
                                <Tooltip title="Ver conversaciones">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<ChatIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => handleOpenAgent(agent)}
                                        sx={{ fontSize: 12, textTransform: 'none', borderRadius: 1.5,
                                            borderColor: '#007AFF', color: '#007AFF',
                                            '&:hover': { bgcolor: '#007AFF0d' } }}
                                    >
                                        Ver
                                    </Button>
                                </Tooltip>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {/* ── Drawer nivel 2: conversaciones del agente ── */}
            <Drawer
                anchor="right"
                open={drawerAgentOpen}
                onClose={handleCloseAgent}
                PaperProps={{ sx: { width: 440, display: 'flex', flexDirection: 'column' } }}
            >
                <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #e5e7eb',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: '#007AFF22', color: '#007AFF', fontWeight: 700, fontSize: 14 }}>
                            {getInitials(selectedAgent?.agentName ?? '')}
                        </Avatar>
                        <Box>
                            <Typography variant="body1" fontWeight={700}>{selectedAgent?.agentName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {selectedAgent?.totalConversations} conversaciones · {selectedAgent?.businessLineName}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Analiza con IA las conversaciones del consultor en el período">
                            <span>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={analyzing}
                                    startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                                    onClick={handleAnalyzeAgent}
                                    sx={{
                                        fontSize: 12, textTransform: 'none', borderRadius: 1.5,
                                        bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' },
                                    }}
                                >
                                    {analyzing ? 'Analizando…' : 'Analizar'}
                                </Button>
                            </span>
                        </Tooltip>
                        <IconButton size="small" onClick={handleCloseAgent}><CloseIcon fontSize="small" /></IconButton>
                    </Box>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#f8fafc' }}>
                    {(analyzing || analysis) && (
                        <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid #e9d5ff', bgcolor: '#faf5ff' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <AutoAwesomeIcon sx={{ fontSize: 18, color: '#7c3aed' }} />
                                <Typography variant="body2" fontWeight={700} color="#6d28d9">Análisis IA del consultor</Typography>
                            </Box>
                            {analyzing ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Skeleton width="40%" height={18} />
                                    <Skeleton width="90%" height={14} />
                                    <Skeleton width="80%" height={14} />
                                </Box>
                            ) : analysis ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={`Cliente: ${analysis.clientFeeling?.label ?? 'n/d'} (${analysis.clientFeeling?.score ?? 0})`}
                                            size="small"
                                            sx={{ fontWeight: 700, color: '#fff', bgcolor: feelingColor(analysis.clientFeeling?.label) }}
                                        />
                                        <Chip
                                            label={`Desempeño: ${analysis.agentPerformance?.score ?? 0}/100`}
                                            size="small"
                                            sx={{ fontWeight: 700, bgcolor: '#ede9fe', color: '#6d28d9' }}
                                        />
                                        <Chip label={`Tono ${analysis.agentPerformance?.tono ?? 0}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                                        <Chip label={`Claridad ${analysis.agentPerformance?.claridad ?? 0}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                                        <Chip label={`Resolución ${analysis.agentPerformance?.resolucion ?? 0}`} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                                    </Box>
                                    {analysis.feedback && (
                                        <Typography variant="body2" sx={{ fontSize: 13, color: '#374151' }}>
                                            {analysis.feedback}
                                        </Typography>
                                    )}
                                    {analysis.strengths?.length > 0 && (
                                        <Box>
                                            <Typography variant="caption" fontWeight={700} color="#16a34a">Fortalezas</Typography>
                                            <ul style={{ margin: '2px 0 0 0', paddingLeft: 18 }}>
                                                {analysis.strengths.map((s, i) => (
                                                    <li key={i} style={{ fontSize: 12, color: '#374151' }}>{s}</li>
                                                ))}
                                            </ul>
                                        </Box>
                                    )}
                                    {analysis.improvements?.length > 0 && (
                                        <Box>
                                            <Typography variant="caption" fontWeight={700} color="#d97706">Oportunidades de mejora</Typography>
                                            <ul style={{ margin: '2px 0 0 0', paddingLeft: 18 }}>
                                                {analysis.improvements.map((s, i) => (
                                                    <li key={i} style={{ fontSize: 12, color: '#374151' }}>{s}</li>
                                                ))}
                                            </ul>
                                        </Box>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        {analysis.conversationsAnalyzed ?? 0} conversaciones analizadas
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                    )}
                    {loadingConversations ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {[...Array(5)].map((_, i) => (
                                <Box key={i} sx={{ display: 'flex', gap: 1.5, p: 1.5,
                                    borderRadius: 2, border: '1px solid #e5e7eb', alignItems: 'center' }}>
                                    <Skeleton variant="circular" width={36} height={36} />
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton width="50%" height={16} />
                                        <Skeleton width="70%" height={14} sx={{ mt: 0.5 }} />
                                    </Box>
                                    <Skeleton width={60} height={30} sx={{ borderRadius: 1 }} />
                                </Box>
                            ))}
                        </Box>
                    ) : agentConversations.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                            justifyContent: 'center', height: 200, gap: 1, color: 'text.secondary' }}>
                            <ChatIcon sx={{ fontSize: 40, opacity: 0.25 }} />
                            <Typography variant="body2">Sin conversaciones en este período</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {agentConversations.map(conv => (
                                <Box
                                    key={conv.assignmentId}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: '1px solid #dbe3ee',
                                        borderLeft: conv.isActive ? '4px solid #22c55e' : '4px solid #cbd5e1',
                                        bgcolor: '#fff',
                                        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
                                        '&:hover': {
                                            bgcolor: '#f8fbff',
                                            borderColor: '#c7d4e3',
                                            boxShadow: '0 2px 4px rgba(15, 23, 42, 0.08)',
                                        },
                                    }}
                                >
                                    <Avatar sx={{ width: 36, height: 36, fontSize: 13,
                                        bgcolor: conv.isActive ? '#22c55e22' : '#e5e7eb',
                                        color: conv.isActive ? '#22c55e' : '#9ca3af', fontWeight: 700 }}>
                                        {getInitials(conv.clientName)}
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap>
                                            {conv.clientName || fmtPhone(conv.clientPhone)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {fmtPhone(conv.clientPhone)} · {fmtDate(conv.assignedAt)}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={conv.isActive ? 'Activa' : 'Finalizada'}
                                                size="small"
                                                sx={{
                                                    height: 18, fontSize: 10, fontWeight: 600,
                                                    bgcolor: conv.isActive ? '#22c55e22' : '#e5e7eb',
                                                    color: conv.isActive ? '#16a34a' : '#6b7280',
                                                }}
                                            />
                                            {conv.flowCompleted && (
                                                <Chip label="Completada" size="small"
                                                    sx={{ height: 18, fontSize: 10, fontWeight: 600,
                                                        bgcolor: '#007AFF22', color: '#007AFF' }} />
                                            )}
                                            {conv.humanControl && (
                                                <Chip label="Control humano" size="small"
                                                    sx={{ height: 18, fontSize: 10, fontWeight: 600,
                                                        bgcolor: '#f59e0b22', color: '#d97706' }} />
                                            )}
                                        </Box>
                                    </Box>
                                    <Tooltip title={conv.conversationId ? 'Ver mensajería' : 'Sin conversación'}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={!conv.conversationId}
                                                onClick={() => handleOpenChat(conv)}
                                                sx={{ color: '#007AFF' }}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </Drawer>

            {/* ── Drawer nivel 3: chat ── */}
            <Drawer
                anchor="right"
                open={drawerChatOpen}
                onClose={handleCloseChat}
                PaperProps={{ sx: { width: 500, display: 'flex', flexDirection: 'column' } }}
            >
                <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #e5e7eb',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="body1" fontWeight={700}>
                            {selectedConv?.clientName || fmtPhone(selectedConv?.clientPhone)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {fmtPhone(selectedConv?.clientPhone)} · Asignado el {selectedConv ? fmtDate(selectedConv.assignedAt) : ''}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={handleCloseChat}><CloseIcon fontSize="small" /></IconButton>
                </Box>
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <WhatsAppChat
                        conversation={null}
                        onClose={handleCloseChat}
                        height="100%"
                        maxWidth="100%"
                        hideHeader={true}
                    />
                </Box>
            </Drawer>
        </Box>
    );
};

export default InformesAIPage;
